import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Box, Button, Heading, Text } from '@indeed/ifl-components';
import { Helmet } from 'react-helmet';
import PageTransition from '../../../components/PageTransition/PageTransition';
import JobInfoHeader from '../components/JobInfoHeader';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { AvroOptional } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewButtonClickEvent';

/**
 * Resume page for Auto Apply flow.
 * Allows editing/selecting resume for the current draft.
 * Design based on auto-apply-setup/resume page.
 */
const ResumePage: React.FC = () => {
    const navigate = useNavigate();
    const { draftId } = useParams<{ draftId: string }>();
    const location = useLocation();
    const eventLogger = useEventLogger();

    const handleUpdate = () => {
        eventLogger.logReviewButtonClick?.({
            currentPage: location.pathname,
            button: 'update',
            draftId: AvroOptional.string(draftId),
        });

        // TODO: Save resume selection via GraphQL mutation

        // Use replace: true to remove edit page from browser history
        // so pressing back from review doesn't return to the edit page
        void navigate(`/auto-apply/${draftId}/review${location.search}`, { replace: true });
    };

    return (
        <PageTransition pageKey={`resume-edit-${draftId}`}>
            <Helmet>
                <title>Edit Resume - Indeed</title>
            </Helmet>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minBlockSize: '100vh',
                    backgroundColor: 'color.background.surface',
                }}
            >
                {/* Job Info Header */}
                <JobInfoHeader />

                {/* Main Content */}
                <Box
                    sx={{
                        flex: 1,
                        padding: '24px 16px',
                        maxInlineSize: '400px',
                        margin: '0 auto',
                        inlineSize: '100%',
                    }}
                >
                    {/* Header */}
                    <Heading level={1} sx={{ marginBlockEnd: 2 }}>
                        Add a resume
                    </Heading>
                    <Text level={2} sx={{ color: 'color.text.secondary', marginBlockEnd: 4 }}>
                        We'll use the resume from your profile as a foundation in the jobs we apply
                        to for you.
                    </Text>

                    {/* Resume Options */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                        }}
                    >
                        Placeholder for resume options
                    </Box>

                    {/* Update Button */}
                    <Box sx={{ marginBlockStart: 4 }}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={handleUpdate}
                            sx={{ inlineSize: '100%' }}
                        >
                            {'Update'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </PageTransition>
    );
};

export default ResumePage;
